// src/utils/invoiceRefundDialog.js — refunddialog-4e9b7c25 (R1d-2)
// The thinking behind the "Why are you refunding?" dialog (components/common/InvoiceRefundDialog.js),
// kept out of the component so it can be tested line by line. Nothing here decides a refund: the
// server does (refundInvoicePayment). These rules only pre-fill, cap and word the screen, and build
// the one request the dialog sends.
//
// THE RULES
//   - One request id per dialog open: a double click or a retry can never make two refunds, and a
//     reply lost on the way back is answered "already processed" by the server.
//   - The amount is capped the way the server caps it: what is left on the chosen payment, what is
//     left on the invoice, and (reduce the sale) what is left of the sale after earlier credit notes.
//   - "Reduce the sale" is greyed out, in the server's own words, where the screen can already see
//     it would be refused (tax not recorded; no GST/HST number). The QuickBooks-only refusals can't
//     be foreseen — the server reads QuickBooks — so they arrive at Confirm and are shown word for word.
//   - A reply is READ, not assumed: a failed card refund comes back as an ordinary reply.
import { DEPOSIT_OFFLINE_METHODS } from './quoteSchema';
import { MSG, r2, isCashPaymentRow, summarizeRefundable, sourceRemaining, taxUnknownReason } from './invoiceRefund';

const REASONS = Object.freeze({ REDUCE_SALE: 'reduceSale', RETURN_PAYMENT: 'returnPayment' });

// Marcus, 2026-10-10 — shown under the bank-account picker when the refund goes back to a card.
const CARD_BANK_HINT = 'Card refunds are taken from your Stripe balance. Choose the bank account your Stripe payouts go to.';
const RETURN_INSTEAD_LABEL = 'Use Return a payment instead';
const CARD_FAILED_MESSAGE = 'The card refund failed at Stripe, so nothing was recorded on this invoice. You can try again, or refund another way.';
const GENERIC_ERROR = 'Could not issue the refund. Please try again.';
const INVOICE_CHANGED = 'This invoice changed since the page was opened, so the payment you picked can no longer be matched. Close this, refresh the page and try again.';

const METHOD_OPTIONS = [
    { value: DEPOSIT_OFFLINE_METHODS.CHEQUE, label: 'Cheque' },
    { value: DEPOSIT_OFFLINE_METHODS.ETRANSFER, label: 'E-transfer' },
    { value: DEPOSIT_OFFLINE_METHODS.CASH, label: 'Cash' },
    { value: DEPOSIT_OFFLINE_METHODS.OTHER, label: 'Other' },
];
const METHOD_WORDS = { cheque: 'by cheque', etransfer: 'by e-transfer', cash: 'in cash', other: 'another way', card: "to the customer's card" };

/** A fresh id the server accepts (/^[A-Za-z0-9_-]{8,128}$/). Called once per dialog open. */
function newRequestId() {
    const c = typeof window !== 'undefined' ? window.crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;
}

const ms = (d) => {
    if (!d) return 0;
    if (typeof d.toMillis === 'function') return d.toMillis();
    if (typeof d.seconds === 'number') return d.seconds * 1000;
    const t = new Date(d).getTime();
    return Number.isNaN(t) ? 0 : t;
};
/** Is the line at this position still the payment the operator clicked? (The page's copy can be stale.) */
function sameLine(clicked, fresh) {
    if (!clicked || !fresh) return false;
    const same = (k) => (clicked[k] ?? null) === (fresh[k] ?? null);
    return Number(clicked.amount) === Number(fresh.amount) && same('method') && same('reference')
        && same('qbPaymentId') && same('stripePaymentIntentId') && ms(clicked.date) === ms(fresh.date);
}

/**
 * What can be refunded from the chosen payment line, capped as the server caps it.
 * @returns {{ok:false} | {ok:true, row, card:boolean, lineLeft, invoiceLeft, saleLeft, returnMax, reduceMax}}
 */
function refundLimits(invoice, sourceIndex) {
    const inv = invoice || {};
    const history = Array.isArray(inv.paymentHistory) ? inv.paymentHistory : [];
    const row = Number.isInteger(sourceIndex) && sourceIndex >= 0 ? history[sourceIndex] || null : null;
    if (!row || row.depositQuoteId || !isCashPaymentRow(row)) return { ok: false };
    const invoiceLeft = r2(Math.max(0, Math.min(summarizeRefundable(history).refundable, Number(inv.amountPaid) || 0)));
    const src = sourceRemaining(history, row);
    const lineLeft = r2(Math.max(0, src ? src.remaining : Number(row.amount) || 0));
    const saleLeft = r2(Math.max(0, (Number(inv.total) || 0) - (Number(inv.creditedTotal) || 0)));
    const returnMax = r2(Math.min(lineLeft, invoiceLeft));
    return { ok: true, row, card: !!row.stripePaymentIntentId, lineLeft, invoiceLeft, saleLeft, returnMax, reduceMax: r2(Math.min(returnMax, saleLeft)) };
}
const maxFor = (limits, reason) => (!limits || !limits.ok ? 0 : reason === REASONS.REDUCE_SALE ? limits.reduceMax : limits.returnMax);

/** Why "reduce the sale" can't be offered on this invoice — the server's own message — or null. */
function reduceSaleBlock(invoice, invoiceId, companySettings) {
    const why = taxUnknownReason(invoice, invoiceId);
    if (why) return why === 'importedNoTax' ? MSG.TAX_UNKNOWN_IMPORT : MSG.TAX_UNKNOWN;
    if (Number(invoice.taxAmount) > 0 && !String((companySettings && companySettings.taxId) || '').trim()) return MSG.NO_GST_NUMBER;
    return null;
}

/** Will this refund be posted to QuickBooks (so a bank account is needed)? Mirrors the server's qbApplies. */
function bankPickerApplies({ companySettings, invoice, row, reason }) {
    const realQbPayment = !!(row && row.qbPaymentId && row.qbPaymentId !== 'skipped');
    return !!companySettings && companySettings.quickbooksConnected === true && !!(invoice && invoice.qbInvoiceId)
        && (reason === REASONS.REDUCE_SALE || realQbPayment);
}

const parseAmount = (text) => r2(parseFloat(String(text).replace(/[^0-9.-]/g, '')) || 0);
/** True when the typed amount can't be sent: not above zero, or above what is left. */
const amountInvalid = (text, max) => { const a = parseAmount(text); return !(a > 0) || a > max + 0.005; };

/** The ONE request the dialog sends — the same shape functions/scripts/callRefundInvoicePayment.js sends. */
function buildRefundPayload({ invoiceId, reason, card, method, amount, sourceIndex, requestId, emailCreditNote, bankAccountId }) {
    return {
        invoiceId, offline: !card, requestId,
        ...(card ? {} : { method }),
        amount: r2(amount),
        reason,
        sourceIndex,
        emailCreditNote: reason === REASONS.REDUCE_SALE ? emailCreditNote !== false : false,
        ...(bankAccountId ? { bankAccountId } : {}),
    };
}

/**
 * Read the server's reply. A failed card refund is NOT an error — it is a reply saying "failed".
 * @returns {{kind:'done'|'pending'|'failed', creditNoteNumber:string|null, already:boolean, message:string|null}}
 */
function readRefundReply(data) {
    const d = data || {};
    if (d.success === false || d.status === 'failed') return { kind: 'failed', creditNoteNumber: null, already: false, message: CARD_FAILED_MESSAGE };
    if (d.status === 'pending') return { kind: 'pending', creditNoteNumber: null, already: false, message: null };
    return { kind: 'done', creditNoteNumber: d.creditNoteNumber || null, already: d.alreadyProcessed === true, message: null };
}

/**
 * Read a refusal. The message is shown word for word. offerReturnPayment: the server said to use
 * "Return a payment" instead (every QuickBooks-only refusal, and the tax ones). needsNewId: this
 * request id is spent (an earlier attempt with it failed), so the next try must use a new one.
 */
function readRefundError(err) {
    const message = (err && typeof err.message === 'string' && err.message.trim()) || GENERIC_ERROR;
    return { message, offerReturnPayment: /use "Return a payment" instead/i.test(message), needsNewId: message === MSG.FAILED_BEFORE };
}

/** The plain-English lines of the summary step, in order. */
function summaryRows({ reason, amountText, card, method, bankName, creditNoteEmail, emailTo, formatMoney }) {
    const reduce = reason === REASONS.REDUCE_SALE;
    const rows = [
        { label: 'Why', value: reduce ? 'Reduce the sale' : 'Return a payment' },
        { label: 'Amount', value: formatMoney(parseAmount(amountText)) },
        { label: 'How', value: `Refund ${METHOD_WORDS[card ? 'card' : method] || ''}`.trim() },
    ];
    if (bankName) rows.push({ label: 'Bank account', value: bankName });
    rows.push({ label: 'What happens', value: reduce
        ? 'A credit note is issued for this amount, tax included. The invoice stays paid.'
        : 'The invoice reopens and shows this amount as owing again.' });
    // sumbox-5c1f8e36 -- isEmailChoice: the dialog draws this row as the checkbox itself.
    if (reduce) rows.push({ label: 'Credit note email', isEmailChoice: true, value: creditNoteEmail ? `Emailed to ${emailTo || "the customer's email on file"}` : 'Not emailed' });
    return rows;
}

export {
    REASONS, CARD_BANK_HINT, RETURN_INSTEAD_LABEL, CARD_FAILED_MESSAGE, GENERIC_ERROR, INVOICE_CHANGED, METHOD_OPTIONS,
    newRequestId, sameLine, refundLimits, maxFor, reduceSaleBlock, bankPickerApplies, parseAmount, amountInvalid,
    buildRefundPayload, readRefundReply, readRefundError, summaryRows,
};
